import type { $Fetch } from "nitropack";

// ---- Status / decision string unions (kept in sync with backend) ----

export type CreditNoteRequestStatus =
    | 'requested'
    | 'in_progress'
    | 'completed'
    | 'rejected';

export type CreditNoteRequestDecision =
    | 'no_refund'
    | 'total_refund'
    | 'partial_refund';

// ---- Submit (freelance) ----

export interface SubmitRequestBody {
    reason: string;
}

export interface SubmitRequestResponse {
    request_id: string;
    status: string;
}

// ---- List mine (freelance) ----
// Matches `UserRequestSummary` in
// `api/crates/rest/src/v1/invoices/credit_note_request.rs`.
// `invoice_number` is an i64 on the backend; JSON serialization yields a raw
// number which JS handles fine for typical invoice number ranges.
// `invoice_amount_total_cents` is an i32; helpers divide by 100 for display.
// `status` is the raw backend string (may be any `CreditNoteRequestStatus`).
export interface UserRequestSummary {
    id: string;
    invoice_id: string;
    invoice_number: number;
    invoice_amount_total_cents: number;
    status: string;
    company_name: string;
    created_at: string;
}

// ---- Admin list ----
// Matches `AdminSummary` in
// `api/crates/rest/src/v1/admin/credit_note_requests.rs`.
export interface AdminSummary {
    id: string;
    invoice_id: string;
    invoice_number: number;
    invoice_amount_total_cents: number;
    freelance_full_name: string;
    company_name: string;
    status: string;
    reason_excerpt: string;
    created_at: string;
}

export interface AdminListResponse {
    items: AdminSummary[];
    total: number;
    page: number;
    per_page: number;
}

// ---- Admin detail ----
// Matches `AdminDetailResponse` in `api/crates/rest/src/v1/admin/credit_note_requests.rs`.
// Backend serializes each section to a raw `serde_json::Value`, so the shapes
// below mirror the underlying Rust entities (`CreditNoteRequest`,
// `CreditNoteRequestAction`) and the admin invoice / parties projections.

export interface AdminDetailRequest {
    id: string;
    invoice_id: string;
    requested_by_user_id: string;
    reason: string;
    status: CreditNoteRequestStatus;
    decision: CreditNoteRequestDecision | null;
    partial_refund_amount: string | null;
    admin_notes: string | null;
    checklist: Record<string, boolean>;
    created_credit_note_id: string | null;
    rejection_reason: string | null;
    created_at: string;
    updated_at: string;
    completed_at: string | null;
}

export interface AdminDetailFreelance {
    user_id: string;
    first_name: string;
    last_name: string;
    email: string;
    phone: string | null;
}

export interface AdminDetailCompany {
    company_id: string;
    name: string;
    siren: string | null;
    contact_email: string | null;
    contact_phone: string | null;
    contact_full_name: string | null;
}

export interface AdminDetailInvoiceItem {
    title: string;
    quantity: string;
    unit_price: string;
    vat_rate: string;
}

export interface AdminDetailInvoice {
    invoice_id: string;
    number: string;
    amount_total: string;
    amount_due: string;
    created_at: string;
    items: AdminDetailInvoiceItem[];
}

export interface AdminDetailAction {
    id: string;
    user_id: string;
    action_type: string;
    payload: Record<string, unknown>;
    created_at: string;
}

export interface AdminDetailResponse {
    request: AdminDetailRequest;
    freelance: AdminDetailFreelance;
    company: AdminDetailCompany;
    invoice: AdminDetailInvoice;
    actions: AdminDetailAction[];
}

// ---- Mutations ----

export interface AdminPatchBody {
    checklist?: Record<string, boolean>;
    admin_notes?: string;
}

export interface AdminApproveBody {
    decision: CreditNoteRequestDecision;
    /** Required when `decision === 'partial_refund'`. */
    partial_amount?: number;
}

export interface AdminRejectBody {
    rejection_reason: string;
}

export interface OkResponse {
    ok: true;
}

export const CreditNoteRequestRepository = (client: $Fetch) => ({
    submit: (missionId: string, invoiceId: string, body: SubmitRequestBody) =>
        client<SubmitRequestResponse>(
            `/v1/invoices/${missionId}/${invoiceId}/credit-note-request`,
            { method: 'POST', body },
        ),

    listMine: () =>
        client<UserRequestSummary[]>('/v1/me/credit-note-requests', {
            method: 'GET',
        }),

    adminList: (params?: {
        status?: CreditNoteRequestStatus;
        page?: number;
        per_page?: number;
    }) => {
        const qs = new URLSearchParams();
        if (params?.status) qs.append('status', params.status);
        if (params?.page !== undefined) qs.append('page', String(params.page));
        if (params?.per_page !== undefined) qs.append('per_page', String(params.per_page));
        const suffix = qs.toString() ? `?${qs.toString()}` : '';
        return client<AdminListResponse>(`/v1/admin/credit-note-requests${suffix}`, {
            method: 'GET',
        });
    },

    adminGet: (id: string) =>
        client<AdminDetailResponse>(`/v1/admin/credit-note-requests/${id}`, {
            method: 'GET',
        }),

    adminPatch: (id: string, body: AdminPatchBody) =>
        client<OkResponse>(`/v1/admin/credit-note-requests/${id}`, {
            method: 'PATCH',
            body,
        }),

    adminApprove: (id: string, body: AdminApproveBody) =>
        client<OkResponse>(`/v1/admin/credit-note-requests/${id}/approve`, {
            method: 'POST',
            body,
        }),

    adminReject: (id: string, body: AdminRejectBody) =>
        client<OkResponse>(`/v1/admin/credit-note-requests/${id}/reject`, {
            method: 'POST',
            body,
        }),
});
